/**
 * LabTwinPage — iter601 Lab Twin Simulator.
 *
 * 9 labs · 8 dietary scenario knobs · 30/60/90-day what-if projections + AI rationale.
 * Manual entry + PDF auto-import from clinical lab reports.
 * Pro feature (gated server-side; this page degrades gracefully on free).
 */
import React, { useEffect, useState, useRef } from "react";
import MedModuleNotes from "@/components/meds/MedModuleNotes";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { MobileTopBar } from "@/components/MobileTopBar";
import {
  FlaskConical, Upload, Sparkles, Loader2, Save, FileText, Activity, Database, CheckCircle2, XCircle,
} from "lucide-react";
import MarkerReportButton from "@/components/MarkerReportButton";

// iter899 — Map LabTwin form keys → canonical marker codes so a report
// against a field links to the same row that lives in `lab_entries`.
const KEY_TO_CANONICAL = {
  hba1c_pct:              "hba1c",
  fasting_glucose_mmol_l: "fasting_glucose",
  ldl_mmol_l:             "ldl_cholesterol",
  hdl_mmol_l:             "hdl_cholesterol",
  triglycerides_mmol_l:   "triglycerides",
  hscrp_mg_l:             "hs_crp",
  homocysteine_umol_l:    "homocysteine",
  vitamin_b12_pmol_l:     "vitamin_b12",
  vitamin_d_ng_ml:        "vitamin_d_25oh",
  // iter1113 — закриване на gap между lab-upload и digital twins
  folate_nmol_l:          "folate",
  zinc_umol_l:            "zinc",
  dhea_s_umol_l:          "dhea_s",
  igf1_ng_ml:             "igf1",
  wbc_10e9_l:             "wbc",
  platelets_10e9_l:       "platelets",
  mcv_fl:                 "mcv",
  // Селен няма canonical code в lab_unification; остава без mapping — все пак може да се запише в lab_snapshots.
};

// iter695 — БГ етикети за aggregate sources (mirror на /api/lab-twin/aggregate)
const SOURCE_LABELS = {
  unified_lab_entries: "Централен лабораторен архив (единна точка)",  // iter894
  core_labs:           "Стандартни (HbA1c, липиди, B12, D)",
  dutch:               "DUTCH хормонален панел",
  genomic:             "ДНК / SNP анализ",
  thyroid:             "Щитовидна (TSH, FT4, anti-TPO)",
  metabolic:           "Метаболитен (инсулин, ApoB)",
  liver_kidney:        "Черен дроб и бъбреци",
  hormones:            "Серумни хормони (E2, Prog, T)",
  medical_images:      "Медицински изображения",
  legacy_profile_labs: "Стари данни от профила",
};

const LABS = [
  { key: "hba1c_pct",              label: "HbA1c",             unit: "%",       opt_range: "4.5-5.6", step: 0.1, hint: "Отключва Метаболитен близнак — показва тримесечния контрол на кръвната захар." },
  { key: "fasting_glucose_mmol_l", label: "Кръвна захар (гладно)", unit: "mmol/L", opt_range: "3.9-5.5", step: 0.1, hint: "Отключва Метаболитен близнак — ранен сигнал за инсулинова резистентност." },
  { key: "ldl_mmol_l",             label: "LDL холестерол",    unit: "mmol/L", opt_range: "<3.0", step: 0.1, hint: "Отключва Кардио близнак — съотношението LDL/HDL влиза в показателя за липидите." },
  { key: "hdl_mmol_l",             label: "HDL холестерол",    unit: "mmol/L", opt_range: ">1.2", step: 0.1, hint: "Отключва Кардио близнак — защитен холестерол." },
  { key: "triglycerides_mmol_l",   label: "Триглицериди",      unit: "mmol/L", opt_range: "<1.7", step: 0.1, hint: "Отключва Кардио и Чернодробен близнак — показател за метаболитен синдром." },
  { key: "hscrp_mg_l",             label: "hsCRP (възпаление)", unit: "mg/L",  opt_range: "<1.0", step: 0.1, hint: "Отключва Възпалителен близнак и Здраве на гърда — показател за системно възпаление." },
  { key: "homocysteine_umol_l",    label: "Хомоцистеин",        unit: "µmol/L", opt_range: "5-15", step: 0.5, hint: "Отключва Кардио близнак — метилиране и сърдечно-съдов риск." },
  { key: "vitamin_b12_pmol_l",     label: "Витамин B12",        unit: "pmol/L", opt_range: ">221", step: 5, hint: "Отключва Хематологичен и Неврологичен близнак — узряване на червени кръвни клетки." },
  { key: "vitamin_d_ng_ml",        label: "Витамин D (25-OH)", unit: "ng/mL", opt_range: "30-50", step: 1, hint: "Отключва Здраве на гърда, Костен и Хормонален близнак — имунитет и калций." },
  // iter1113 — 8 нови полета: закриват пропуските между качването на лабораторни изследвания и дигиталните близнаци.
  { key: "folate_nmol_l",          label: "Фолат",              unit: "nmol/L", opt_range: "10-45", step: 1, hint: "Отключва Хематологичен близнак — макроцитна анемия и метилиране." },
  { key: "zinc_umol_l",            label: "Цинк (серум)",       unit: "µmol/L", opt_range: "10.7-18", step: 0.5, hint: "Отключва Хематологичен и Зъбен близнак — имунитет, вкус и зарастване." },
  { key: "selenium_ug_l",          label: "Селен",              unit: "µg/L",   opt_range: "70-150", step: 5, hint: "Поддържа Щитовиден близнак — ко-фактор за превръщане на T4 в T3." },
  { key: "dhea_s_umol_l",          label: "DHEA-S",             unit: "µmol/L", opt_range: "възраст/пол", step: 0.1, hint: "Отключва Хормонален близнак — надбъбречен резерв и продължително натоварване." },
  { key: "igf1_ng_ml",             label: "IGF-1",              unit: "ng/mL",  opt_range: "възраст/пол", step: 5, hint: "Отключва Здраве на гърда и Биологична възраст — растежен фактор." },
  { key: "wbc_10e9_l",             label: "Левкоцити (WBC)",    unit: "×10⁹/L", opt_range: "4-11", step: 0.1, hint: "Отключва Хематологичен близнак — имунна защита." },
  { key: "platelets_10e9_l",       label: "Тромбоцити (PLT)",   unit: "×10⁹/L", opt_range: "150-450", step: 10, hint: "Отключва Хематологичен близнак — съсирване на кръвта." },
  { key: "mcv_fl",                 label: "MCV (среден обем на еритроцит)", unit: "fL", opt_range: "80-100", step: 0.5, hint: "Отключва Хематологичен близнак — разграничава микроцитна от макроцитна анемия." },
];

const SCENARIOS = [
  { key: "carb_reduction_g_per_day",         label: "Намаляване на въглехидрати", unit: "г/ден", max: 150, step: 10 },
  { key: "soluble_fiber_g_per_day",          label: "Добавени фибри",              unit: "г/ден", max: 30,  step: 2 },
  { key: "omega3_servings_per_week",         label: "Омега-3 порции (риба/семена)", unit: "седм.", max: 7,  step: 1 },
  { key: "saturated_fat_reduction_g_per_day",label: "Намаляване на наситени мазнини", unit: "г/ден", max: 20, step: 1 },
  { key: "added_sugar_reduction_g_per_day",  label: "Намаляване на добавена захар", unit: "г/ден", max: 100, step: 5 },
  { key: "mediterranean_adherence_pct",      label: "Средиземноморска адхеренция", unit: "%", max: 100, step: 5 },
  { key: "vit_d_supplement_iu_per_day",      label: "Витамин D добавка",            unit: "IU/ден", max: 4000, step: 500 },
];

export default function LabTwinPage() {
  const { API } = useAuth();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [baseline, setBaseline] = useState({});
  const [scenario, setScenario] = useState({
    carb_reduction_g_per_day: 0, soluble_fiber_g_per_day: 0,
    omega3_servings_per_week: 0, saturated_fat_reduction_g_per_day: 0,
    added_sugar_reduction_g_per_day: 0, mediterranean_adherence_pct: 0,
    vit_b_complex_supplement: false, vit_d_supplement_iu_per_day: 0,
  });
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [aggregate, setAggregate] = useState(null);          // iter695
  const [aggregateLoading, setAggregateLoading] = useState(true);
  const fileRef = useRef();

  // Load latest snapshot on mount
  useEffect(() => {
    axios.get(`${API}/lab-twin/snapshot`).then(({ data }) => {
      if (data?.snapshot) {
        const s = { ...data.snapshot };
        delete s._id; delete s.timestamp; delete s.notes;
        setBaseline(s);
      }
    }).catch(() => {});
  }, [API]);

  // iter695 — Load aggregate (all 9 lab data sources)
  const refreshAggregate = React.useCallback(async () => {
    setAggregateLoading(true);
    try {
      const { data } = await axios.get(`${API}/lab-twin/aggregate`);
      setAggregate(data);
    } catch (e) {
      // silent — best effort
    } finally {
      setAggregateLoading(false);
    }
  }, [API]);

  useEffect(() => { refreshAggregate(); }, [refreshAggregate]);

  const updateBaseline = (k, v) => setBaseline(b => ({ ...b, [k]: v === "" ? undefined : Number(v) }));

  const saveSnapshot = async () => {
    const filtered = Object.fromEntries(Object.entries(baseline).filter(([, v]) => v != null));
    if (Object.keys(filtered).length === 0) {
      toast.error("Попълни поне един показател");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/lab-twin/snapshot`, filtered);
      toast.success("Запазено ✓");
      refreshAggregate();  // iter695
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
    } finally {
      setSaving(false);
    }
  };

  const importPdf = async (file) => {
    if (!file) return;
    setPdfLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/lab-twin/pdf-import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const cnt = Object.keys(data.extracted || {}).length;
      if (cnt === 0) {
        toast.error("Няма разпознати показатели");
      } else {
        setBaseline(b => ({ ...b, ...data.extracted }));
        const methodLabel = data.method === "vision"
          ? " (AI Vision)"
          : data.method === "hybrid" ? " (текст + AI)" : "";
        toast.success(`${cnt} показателя извлечени${methodLabel} ✓`);
      }
      // iter695 — Refresh aggregate след import
      refreshAggregate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при импорт");
    } finally {
      setPdfLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const runSimulation = async () => {
    const filtered = Object.fromEntries(Object.entries(baseline).filter(([, v]) => v != null));
    if (Object.keys(filtered).length === 0) {
      toast.error("Попълни базови стойности първо");
      return;
    }
    setSimLoading(true);
    try {
      const { data } = await axios.post(`${API}/lab-twin/simulate`, {
        baseline: filtered,
        scenario,
      });
      setSimResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulation грешка");
    } finally {
      setSimLoading(false);
    }
  };

  const cardCls = isDark ? "bg-slate-900/40 border-slate-800" : "bg-white border-slate-200";

  return (
    <div className={`min-h-screen p-4 sm:p-6 ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`} data-testid="lab-twin-page">
      <MobileTopBar title="Лаборатория" fallbackTo="/dashboard" testid="lab-twin-mobile-topbar" />
      <div className="max-w-5xl mx-auto space-y-4">
        <MedModuleNotes module="labs" cardCls={cardCls} isDark={isDark} />
        {/* Header */}
        <div className={`p-4 rounded-2xl border ${cardCls}`}>
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center shrink-0">
                <FlaskConical className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold">Лабораторни изследвания</h1>
                <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  Единният вход за резултати: качване, разчитане и прогноза за 30, 60 и 90 дни.
                  Оценката по всеки показател идва от близнака, който го притежава.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input ref={fileRef} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => importPdf(e.target.files?.[0])} data-testid="lab-twin-pdf-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={pdfLoading} data-testid="lab-twin-pdf-btn">
                {pdfLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                Качване на PDF или снимка
              </Button>
              <Button variant="outline" onClick={() => window.print()} data-testid="lab-twin-print">
                <FileText className="w-4 h-4 mr-1" /> PDF
              </Button>
              <Button variant="outline" onClick={() => navigate("/dashboard/marker-map")}
                data-testid="lab-twin-marker-map-btn">
                Карта на показателите
              </Button>
              <Button variant="outline" onClick={() => navigate("/dashboard/lab-twin-network")}
                data-testid="lab-twin-network-btn">
                Връзки с близнаците
              </Button>
            </div>
          </div>
        </div>

        {/* iter695 — Lab Aggregate Hub: показва всички 9 източника + completeness */}
        {!aggregateLoading && aggregate?.summary && (
          <div
            className={`p-4 rounded-2xl border ${cardCls}`}
            data-testid="lab-aggregate-hub"
          >
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-cyan-500" />
                <h2 className="font-bold text-sm">Всички източници на резултати</h2>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className={isDark ? "text-slate-400" : "text-slate-500"}>
                  {aggregate.summary.sources_with_data.length}/{aggregate.summary.sources_with_data.length + aggregate.summary.missing_sources.length} източника
                </span>
                <span className="font-mono font-bold text-cyan-500">
                  {aggregate.summary.completeness_pct}%
                </span>
              </div>
            </div>

            {/* Progress bar */}
            <div className={`h-2 rounded-full overflow-hidden mb-3 ${isDark ? "bg-slate-800" : "bg-slate-100"}`}>
              <div
                className="h-full bg-gradient-to-r from-cyan-500 to-indigo-500 transition-all"
                style={{ width: `${aggregate.summary.completeness_pct}%` }}
                data-testid="lab-hub-completeness-bar"
              />
            </div>

            {/* Sources grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
              {Object.entries(SOURCE_LABELS).map(([key, label]) => {
                const hasData = aggregate.summary.sources_with_data.includes(key);
                return (
                  <div
                    key={key}
                    className={`flex items-center gap-1.5 text-[11px] px-2 py-1.5 rounded-md ${
                      hasData
                        ? isDark ? "bg-emerald-900/30 text-emerald-300" : "bg-emerald-50 text-emerald-700"
                        : isDark ? "bg-slate-800/50 text-slate-500" : "bg-slate-50 text-slate-400"
                    }`}
                    data-testid={`lab-hub-source-${key}`}
                  >
                    {hasData
                      ? <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                      : <XCircle className="w-3 h-3 flex-shrink-0 opacity-50" />}
                    <span className="truncate">{label}</span>
                  </div>
                );
              })}
            </div>

            {/* Stats row */}
            <div className={`flex justify-between text-[11px] pt-2 border-t ${isDark ? "border-slate-800 text-slate-400" : "border-slate-100 text-slate-500"}`}>
              <span>📊 <strong>{aggregate.summary.total_markers}</strong> активни маркера</span>
              <span>🩻 <strong>{aggregate.summary.medical_images_count}</strong> изображения</span>
              <span>🎯 <strong>{aggregate.summary.marker_coverage_pct}%</strong> марк. покритие</span>
            </div>

            {aggregate.summary.missing_sources.length > 0 && (
              <p className={`text-[11px] mt-2 ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                💡 Качи PDF/снимка по-горе за да допълниш: {
                  aggregate.summary.missing_sources
                    .filter(s => !(s === "medical_images" && aggregate.summary.medical_images_count > 0))
                    .map(s => SOURCE_LABELS[s] || s)
                    .join(", ")
                }
              </p>
            )}
          </div>
        )}

        {/* Baseline labs */}
        <div className={`p-4 rounded-2xl border ${cardCls}`}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-sm">📋 Базови лаборатории</h2>
            <Button onClick={saveSnapshot} disabled={saving} size="sm" className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="lab-twin-save-snapshot">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Запази
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {LABS.map(l => (
              <div key={l.key}>
                <div className="flex items-center justify-between">
                  <label className="text-[10px] uppercase tracking-wider font-bold opacity-70">{l.label}</label>
                  {baseline[l.key] != null && baseline[l.key] !== "" && KEY_TO_CANONICAL[l.key] && (
                    <MarkerReportButton
                      markerCode={KEY_TO_CANONICAL[l.key]}
                      markerLabel={l.label}
                      currentValue={Number(baseline[l.key])}
                      currentUnit={l.unit}
                      size="xs"
                    />
                  )}
                </div>
                <div className="relative">
                  <Input
                    type="number" step={l.step}
                    placeholder={l.opt_range}
                    value={baseline[l.key] ?? ""}
                    onChange={(e) => updateBaseline(l.key, e.target.value)}
                    className="pr-14"
                    data-testid={`lab-twin-input-${l.key}`}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] opacity-50">{l.unit}</span>
                </div>
                {l.hint && (
                  <p
                    className={`text-[10px] mt-1 leading-snug ${isDark ? "text-cyan-300/70" : "text-cyan-700/80"}`}
                    data-testid={`lab-twin-hint-${l.key}`}
                  >
                    💡 {l.hint}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Scenario sliders */}
        <div className={`p-4 rounded-2xl border ${cardCls}`}>
          <h2 className="font-bold text-sm mb-3">🎚 Хранителен сценарий (какво ако…)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SCENARIOS.map(s => (
              <div key={s.key}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium">{s.label}</span>
                  <span className="font-bold text-sky-500">{scenario[s.key]} {s.unit}</span>
                </div>
                <input
                  type="range"
                  min="0" max={s.max} step={s.step}
                  value={scenario[s.key]}
                  onChange={(e) => setScenario(sc => ({ ...sc, [s.key]: Number(e.target.value) }))}
                  className="w-full accent-sky-500"
                  data-testid={`lab-twin-scenario-${s.key}`}
                />
              </div>
            ))}
            <label className="flex items-center gap-2 text-xs col-span-2">
              <input
                type="checkbox"
                checked={scenario.vit_b_complex_supplement}
                onChange={(e) => setScenario(sc => ({ ...sc, vit_b_complex_supplement: e.target.checked }))}
                data-testid="lab-twin-scenario-b-complex"
              />
              💊 B-комплекс добавка (B6 + B9 + B12) — основен лост за понижаване на хомоцистеин
            </label>
          </div>
          <Button onClick={runSimulation} disabled={simLoading} className="w-full mt-4 bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white" data-testid="lab-twin-simulate">
            {simLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            Стартирай симулация
          </Button>
        </div>

        {/* Results */}
        {simResult && (
          <div className={`p-4 rounded-2xl border ${cardCls}`} data-testid="lab-twin-result">
            <h2 className="font-bold text-sm mb-3 flex items-center gap-1">
              <Activity className="w-4 h-4 text-emerald-500" />
              Прогноза 30 / 60 / 90 дни
            </h2>
            <div className="space-y-2">
              {Object.entries(simResult.projections).map(([k, p]) => {
                const lab = LABS.find(l => l.key === k);
                const delta90 = p.day_90?.delta ?? 0;
                const dir = delta90 > 0 ? "text-rose-500" : delta90 < 0 ? "text-emerald-500" : "text-slate-500";
                return (
                  <div key={k} className={`p-2 rounded-lg ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`} data-testid={`lab-twin-row-${k}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold">{lab?.label || k}</span>
                      <span className={`font-bold ${dir}`}>
                        {delta90 > 0 ? "+" : ""}{delta90} {lab?.unit}
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-1 mt-1 text-[10px]">
                      <Cell label="Базово" value={p.baseline} unit={lab?.unit} />
                      <Cell label="30 дни" value={p.day_30?.projected} ci_low={p.day_30?.ci_low} ci_high={p.day_30?.ci_high} unit={lab?.unit} />
                      <Cell label="60 дни" value={p.day_60?.projected} ci_low={p.day_60?.ci_low} ci_high={p.day_60?.ci_high} unit={lab?.unit} />
                      <Cell label="90 дни" value={p.day_90?.projected} ci_low={p.day_90?.ci_low} ci_high={p.day_90?.ci_high} unit={lab?.unit} highlight />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={`mt-3 p-3 rounded-lg text-xs ${isDark ? "bg-indigo-500/10 border border-indigo-500/30" : "bg-indigo-50 border border-indigo-200"}`}>
              <div className="font-bold mb-1">🧠 AI обяснение</div>
              <p className="text-[11px] leading-relaxed">{simResult.rationale_bg}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Cell({ label, value, ci_low, ci_high, unit, highlight }) {
  if (value == null) return <div />;
  return (
    <div className={`p-1 rounded text-center ${highlight ? "bg-sky-500/15" : ""}`}>
      <div className="opacity-50 text-[8px] uppercase">{label}</div>
      <div className="font-bold text-[11px]">{value}{unit ? ` ${unit}` : ""}</div>
      {ci_low != null && ci_high != null && (
        <div className="opacity-50 text-[8px]">[{ci_low}–{ci_high}]</div>
      )}
    </div>
  );
}
